/**
 * components/GateBar
 * ──────────────────────────────────────────────────────────────────────────
 * The amber bar that says a screen is waiting on configuration, not on data:
 * `#E8D3B0` border over `#FDF7EE`, `#8A4212` text, `16px 26px` padding, with
 * the resolving action on the right.
 *
 * A gate is NOT an error. The request was well-formed and the caller was
 * authorised — the workspace simply has not been set up yet, which is why the
 * API answers 412 rather than 4xx-as-failure and why this bar states the
 * missing setting and offers the one button that fixes it.
 *
 * All copy comes from lib/gate.ts, so Reconciliation, the Sync log and
 * Settlement alerts cannot word the same gate three different ways.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { gate as gateColors, neutral } from '../../theme/colors';
import { sans, size as fontSize, leading } from '../../theme/typography';
import { GATE_COPY, type GateKey } from '../../lib/gate';
import Button from '../Button';

export interface GateBarProps {
  gate: GateKey;
  /** Takes the user to the Settings card that resolves it. */
  onResolve: () => void;
  /** A second line explaining what this particular screen cannot show. */
  note?: string;
  /** Sits inside a card and needs no top rule of its own. */
  inCard?: boolean;
}

export function GateBar({ gate, onResolve, note, inCard = false }: GateBarProps) {
  const copy = GATE_COPY[gate];

  return (
    <View style={[styles.bar, inCard && styles.inCard]}>
      <View style={styles.text}>
        <Text style={styles.title}>{copy.message}</Text>
        {!!note && <Text style={styles.note}>{note}</Text>}
      </View>
      <Button label={copy.action} size="modal" variant="primary" onPress={onResolve} />
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    // The row wraps on a narrow viewport so the button never overlaps the text.
    flexWrap: 'wrap',
    paddingVertical: 16,
    paddingHorizontal: 26,
    backgroundColor: gateColors.bg,
  },
  inCard: { borderTopWidth: 1, borderTopColor: neutral.ruleLight },
  text: { flex: 1, minWidth: 220, gap: 2 },
  title: { ...sans('500'), fontSize: fontSize.button, color: gateColors.text },
  note: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: gateColors.text,
  },
});

export default GateBar;
